<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AnimatePresence, motion } from 'motion-v'
import { useDocumentVisibility, useWindowFocus, refDebounced } from '@vueuse/core'
import type { ChatConversationListItem, ChatMessage, CreateMessagePayload } from '@/services/chat/chatHttp.service'
import { chatApi } from '@/services/api'
import MessageBubble from '@/components/messaging/MessageBubble.vue'
import MessageComposer from '@/components/messaging/MessageComposer.vue'
import ConversationMembersModal from '@/components/messaging/ConversationMembersModal.vue'
import Loading from '@/components/common/loading.vue'
import TextInput from '@/components/common/textInput.vue'
import ResourceImage from '@/components/resource/resourceImage.vue'
import MaterialSymbolsGroupRounded from '~icons/material-symbols/group-rounded'
import MaterialSymbolsSearchRounded from '~icons/material-symbols/search-rounded'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'
import MaterialSymbolsArrowDownwardRounded from '~icons/material-symbols/arrow-downward-rounded'
import { vIntersectionObserver } from '@vueuse/components'
import { useChatStore } from '@/config/store/chatStore'
import { useUserStore } from '@/config/store/userStore'
import { withPollViewerState } from '@/services/chat/pollVisibility'

const NEAR_BOTTOM_PX = 120

const { t } = useI18n()
const chatStore = useChatStore()
const userStore = useUserStore()
const visibility = useDocumentVisibility()
const windowFocused = useWindowFocus()

const isPageActive = computed(() =>
  visibility.value === 'visible' && windowFocused.value,
)

const props = defineProps<{
  conversation: ChatConversationListItem | null
  compact?: boolean
}>()

const emits = defineEmits<{
  (e: 'message-sent', message: ChatMessage): void
  (e: 'marked-read'): void
}>()

const messages = ref<ChatMessage[]>([])
const isLoading = ref(false)
const isLoadingMore = ref(false)
const haveMore = ref(true)
const sending = ref(false)
const threadEl = ref<HTMLElement | null>(null)
const replyTo = ref<ChatMessage | null>(null)
const editingMessage = ref<ChatMessage | null>(null)
const searchQuery = defineModel<string>('searchQuery', { default: '' })
const showSearch = defineModel<boolean>('showSearch', { default: false })
const debouncedSearch = refDebounced(searchQuery, 350)

const isNearBottom = ref(true)
const newMessagesWhileAway = ref(0)
const showMembersModal = ref(false)

const title = computed(() => props.conversation?.title || t('modules.chat.title'))
const displayedMessages = computed(() => messages.value)
const showJumpToLatest = computed(() => !isNearBottom.value)
const jumpLabel = computed(() => {
  if (newMessagesWhileAway.value > 0) {
    return t('modules.chat.newMessagesLabel')
  }
  return t('modules.chat.scrollToLatest')
})

const measureNearBottom = () => {
  const el = threadEl.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX
}

const updateNearBottom = () => {
  const near = measureNearBottom()
  isNearBottom.value = near
  if (near && newMessagesWhileAway.value > 0) {
    newMessagesWhileAway.value = 0
    markRead()
  }
}

const onThreadScroll = () => {
  updateNearBottom()
}

const scrollToBottom = async (smooth = false) => {
  await nextTick()
  const el = threadEl.value
  if (!el) return
  if (smooth) {
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }
  else {
    el.scrollTop = el.scrollHeight
  }
  isNearBottom.value = true
  newMessagesWhileAway.value = 0
}

const jumpToLatest = async () => {
  await scrollToBottom(true)
  await markRead()
}

const normalizeIncomingMessage = (message: ChatMessage): ChatMessage => ({
  ...message,
  poll: withPollViewerState(message.poll, String(userStore.user?._id || '')),
  seenBy: message.seenBy || [],
})

const loadMessages = async (reset = true) => {
  if (!props.conversation) return
  if (reset) {
    isLoading.value = true
    messages.value = []
    haveMore.value = true
    replyTo.value = null
    editingMessage.value = null
    newMessagesWhileAway.value = 0
    isNearBottom.value = true
  }
  else {
    isLoadingMore.value = true
  }

  try {
    const before = !reset && messages.value.length
      ? new Date(messages.value[0]._created).toISOString()
      : undefined
    const response = await chatApi.listMessages(props.conversation._id, {
      before,
      limit: 50,
      q: debouncedSearch.value.trim() || undefined,
    }).promise

    if (reset) {
      messages.value = response.map(normalizeIncomingMessage)
      await scrollToBottom(false)
      await markRead()
    }
    else {
      const previousHeight = threadEl.value?.scrollHeight || 0
      messages.value = [...response.map(normalizeIncomingMessage), ...messages.value]
      await nextTick()
      if (threadEl.value) {
        threadEl.value.scrollTop = threadEl.value.scrollHeight - previousHeight
      }
    }
    haveMore.value = response.length >= 50
  }
  catch (err) {
    console.error('Failed to load messages', err)
  }
  finally {
    isLoading.value = false
    isLoadingMore.value = false
  }
}

const markRead = async () => {
  if (!props.conversation) return
  // Do not mark as seen when the tab/window is not active
  if (!isPageActive.value) return
  try {
    const last = messages.value[messages.value.length - 1]
    await chatApi.markRead(props.conversation._id, {
      lastReadMessageId: last?._id,
    }).promise
    chatStore.markLocalRead(props.conversation._id)
    emits('marked-read')
  }
  catch (err) {
    console.error('Failed to mark read', err)
  }
}

/** Called when the page becomes visible again while this thread is open */
const markReadIfActive = () => {
  if (!props.conversation || !isNearBottom.value) return
  markRead()
}

const sendMessage = async (payload: CreateMessagePayload) => {
  if (!props.conversation) return
  const hasContent = String(payload.body || '').replace(/<[^>]+>/g, '').trim()
    || (payload.embeds && payload.embeds.length)
    || (payload.attachmentIds && payload.attachmentIds.length)
    || payload.type === 'gif'
    || payload.type === 'poll'
  if (!hasContent) return
  sending.value = true
  try {
    const message = await chatApi.createMessage(props.conversation._id, payload).promise
    if (!messages.value.some(m => m._id === message._id)) {
      messages.value.push(normalizeIncomingMessage(message))
    }
    replyTo.value = null
    emits('message-sent', message)
    await scrollToBottom(true)
  }
  catch (err) {
    console.error('Failed to send message', err)
  }
  finally {
    sending.value = false
  }
}

const saveEdit = async (payload: { messageId: string; body: string }) => {
  try {
    const updated = await chatApi.updateMessage(payload.messageId, { body: payload.body }).promise
    updateMessage(updated)
    editingMessage.value = null
  }
  catch (err) {
    console.error('Failed to edit message', err)
  }
}

const deleteMessage = async (message: ChatMessage) => {
  try {
    const updated = await chatApi.deleteMessage(message._id).promise
    updateMessage(updated)
  }
  catch (err) {
    console.error('Failed to delete message', err)
  }
}

const reactToMessage = async (message: ChatMessage, emoji: string) => {
  try {
    const existing = message.reactions?.find(r => r.emoji === emoji && r.reactedByMe)
    const result = existing
      ? await chatApi.removeReaction(message._id).promise
      : await chatApi.setReaction(message._id, emoji).promise
    const index = messages.value.findIndex(m => m._id === message._id)
    if (index >= 0) {
      messages.value[index] = { ...messages.value[index], reactions: result.reactions }
    }
  }
  catch (err) {
    console.error('Failed to react', err)
  }
}

/** @returns true if the view stayed stuck to the latest messages */
const appendMessage = (message: ChatMessage): boolean => {
  if (messages.value.some(m => m._id === message._id)) return isNearBottom.value

  const isMine = String(message.senderId) === String(userStore.user?._id)
  messages.value.push(normalizeIncomingMessage(message))

  if (isNearBottom.value || isMine) {
    scrollToBottom(true)
    if (!isMine) markRead()
    return true
  }

  newMessagesWhileAway.value += 1
  return false
}

const updateMessage = (message: ChatMessage) => {
  const index = messages.value.findIndex(m => m._id === message._id)
  if (index >= 0) {
    const normalized = normalizeIncomingMessage(message)
    messages.value[index] = {
      ...normalized,
      seenBy: message.seenBy ?? messages.value[index].seenBy,
    }
  }
}

const updateReactions = (messageId: string, reactions: ChatMessage['reactions']) => {
  const index = messages.value.findIndex(m => m._id === messageId)
  if (index >= 0) {
    messages.value[index] = { ...messages.value[index], reactions }
  }
}

const resolvePeer = (resourceId: string) => {
  const existing = messages.value
    .flatMap(m => m.seenBy || [])
    .find(person => String(person._id) === String(resourceId))
  if (existing) return existing
  if (props.conversation?.otherParticipant
    && String(props.conversation.otherParticipant._id) === String(resourceId)) {
    return props.conversation.otherParticipant
  }
  return {
    _id: resourceId,
    firstname: '',
    lastname: '',
    picture: null,
  }
}

const applyReceipt = (payload: {
  resourceId: string
  lastReadAt: string
  lastReadMessageId?: string | null
}) => {
  if (String(payload.resourceId) === String(userStore.user?._id)) return
  const peer = resolvePeer(payload.resourceId)
  const readAt = payload.lastReadAt ? new Date(payload.lastReadAt).getTime() : 0

  messages.value = messages.value.map((message) => {
    if (String(message.senderId) !== String(userStore.user?._id)) return message
    const messageAt = new Date(message._created).getTime()
    const isRead = (payload.lastReadMessageId && String(payload.lastReadMessageId) === String(message._id))
      || readAt >= messageAt
    const withoutPeer = (message.seenBy || []).filter(person => String(person._id) !== String(payload.resourceId))
    return {
      ...message,
      seenBy: isRead ? [...withoutPeer, peer] : withoutPeer,
    }
  })
}

const loadMoreObserver = (entries: IntersectionObserverEntry[]) => {
  if (!entries[0]?.isIntersecting || isLoading.value || isLoadingMore.value || !haveMore.value) return
  loadMessages(false)
}

const toggleSearch = () => {
  showSearch.value = !showSearch.value
  if (!showSearch.value && searchQuery.value) {
    searchQuery.value = ''
  }
}

const stopListening = chatStore.onEvent((event) => {
  if (!props.conversation?._id) return
  if (event.event === 'receipt.updated'
    && event.payload.conversationId === props.conversation._id) {
    applyReceipt(event.payload)
  }
})

watch(isPageActive, (active) => {
  if (active && props.conversation?._id && isNearBottom.value) {
    markRead()
  }
})

watch(() => props.conversation?._id, (id) => {
  searchQuery.value = ''
  showSearch.value = false
  newMessagesWhileAway.value = 0
  isNearBottom.value = true
  if (id) loadMessages(true)
  else messages.value = []
}, { immediate: true })

watch(debouncedSearch, () => {
  if (props.conversation?._id) loadMessages(true)
})

onUnmounted(() => {
  stopListening()
})

const jumpButtonTransition = {
  initial: { opacity: 0, y: 8, scale: 0.94 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 6, scale: 0.96 },
  transition: { type: 'spring', stiffness: 480, damping: 28, mass: 0.7 },
}

defineExpose({ appendMessage, updateMessage, updateReactions, markRead, markReadIfActive, toggleSearch })
</script>

<template>
  <div
    class="flex flex-col h-full min-w-0"
    :class="compact ? 'bg-transparent' : 'chat-glass'"
    data-cy="chat-thread"
  >
    <div
      v-if="!conversation"
      class="flex-1 flex items-center justify-center text-sm opacity-60 p-6 text-center"
      data-cy="chat-thread-empty"
    >
      {{ t('modules.chat.selectConversation') }}
    </div>
    <template v-else>
      <div
        v-if="!compact"
        class="shrink-0 border-b dark:border-white/10 bg-transparent"
        data-cy="chat-thread-header"
      >
        <div class="min-h-12 px-3 py-1.5 flex items-center gap-2">
          <ResourceImage
            v-if="conversation.type === 'dm' && conversation.otherParticipant"
            :resource="conversation.otherParticipant"
            width="w-9"
            height="h-9"
            disable-link
          />
          <div
            v-else
            class="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center shrink-0"
          >
            <MaterialSymbolsGroupRounded class="w-5 h-5" />
          </div>
          <div class="min-w-0 flex-1">
            <div
              class="font-medium text-sm truncate"
              data-cy="chat-thread-title"
            >
              {{ title }}
            </div>
            <button
              v-if="conversation.type === 'group'"
              type="button"
              class="text-xs opacity-60 hover:opacity-100 hover:text-primary-500 transition-opacity text-left"
              data-cy="chat-view-members-button"
              :title="t('modules.chat.viewMembers')"
              @click="showMembersModal = true"
            >
              {{ t('modules.chat.memberCount', { count: conversation.memberCount }) }}
            </button>
          </div>
          <button
            type="button"
            class="p-1.5 rounded-lg bg-hover shrink-0"
            data-cy="chat-thread-search-toggle"
            :title="showSearch ? t('common.cancel') : t('modules.chat.searchInConversation')"
            :aria-pressed="showSearch"
            @click="toggleSearch"
          >
            <MaterialSymbolsCloseRounded
              v-if="showSearch"
              class="w-5 h-5"
            />
            <MaterialSymbolsSearchRounded
              v-else
              class="w-5 h-5"
            />
          </button>
        </div>
        <div
          v-if="showSearch"
          class="px-3 pb-2.5"
        >
          <TextInput
            v-model="searchQuery"
            data-cy="chat-thread-search-input"
            class="w-full"
            size="small"
            shadow=""
            autofocus
            :placeholder="t('modules.chat.searchInConversation')"
          >
            <template #prepend-icon>
              <MaterialSymbolsSearchRounded class="w-4 h-4 opacity-60" />
            </template>
          </TextInput>
        </div>
      </div>

      <div class="relative flex-1 min-h-0 flex flex-col">
        <div
          ref="threadEl"
          class="flex-1 overflow-y-auto py-3 space-y-0.5 min-h-0 bg-transparent mini-scroll"
          data-cy="chat-message-list"
          @scroll.passive="onThreadScroll"
        >
          <div
            v-if="haveMore"
            v-intersection-observer="loadMoreObserver"
            class="h-4"
          />
          <Loading
            v-if="isLoading || isLoadingMore"
            class="py-2"
          />
          <div
            v-else-if="debouncedSearch.trim() && !displayedMessages.length"
            class="px-4 py-6 text-center text-sm opacity-60"
            data-cy="chat-no-search-results"
          >
            {{ t('modules.chat.noSearchResults') }}
          </div>
          <MessageBubble
            v-for="message in displayedMessages"
            :key="message._id"
            :message="message"
            :compact="compact"
            @reply="replyTo = $event; editingMessage = null"
            @react="reactToMessage"
            @edit="editingMessage = $event; replyTo = null"
            @delete="deleteMessage"
            @updated="updateMessage"
          />
        </div>

        <AnimatePresence>
          <motion.button
            v-if="showJumpToLatest"
            key="jump-latest"
            type="button"
            data-cy="chat-jump-to-latest"
            class="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 inline-flex items-center gap-1.5 max-w-[90%] px-3 py-1.5 rounded-full bg-primary-500 text-white text-xs font-medium shadow-lg shadow-primary-500/25 hover:brightness-110 active:scale-95 transition-[filter,transform]"
            :initial="jumpButtonTransition.initial"
            :animate="jumpButtonTransition.animate"
            :exit="jumpButtonTransition.exit"
            :transition="jumpButtonTransition.transition"
            @click="jumpToLatest"
          >
            <MaterialSymbolsArrowDownwardRounded class="w-4 h-4 shrink-0" />
            <span class="truncate">{{ jumpLabel }}</span>
            <span
              v-if="newMessagesWhileAway > 0"
              class="shrink-0 min-w-5 h-5 px-1 rounded-full bg-white text-primary-600 text-[11px] font-bold flex items-center justify-center"
            >
              {{ newMessagesWhileAway > 99 ? '99+' : newMessagesWhileAway }}
            </span>
          </motion.button>
        </AnimatePresence>
      </div>

      <MessageComposer
        :conversation-id="conversation._id"
        :sending="sending"
        :reply-to="replyTo"
        :editing-message="editingMessage"
        @send="sendMessage"
        @save-edit="saveEdit"
        @cancel-reply="replyTo = null"
        @cancel-edit="editingMessage = null"
      />

      <ConversationMembersModal
        v-model="showMembersModal"
        :conversation-id="conversation._id"
      />
    </template>
  </div>
</template>
